import {applyTransaction} from "@datorama/akita"
import API from "api/API"
import {ApiSystemModuleResult} from "app/Interface/Api/System/ApiSystemModule"
import {ModuleType} from "const/ModuleType"
import Logger from "logger/logger"
import {useEffect, useState} from "react"
import {Subscription} from "rxjs"
import {systemModuleQuery, systemModuleStore} from "store/system/module/system.module.store"
import {onEmit} from "utils/onEmit"

export const subscribeSystemModule = {
  isLoading: 'isLoading',
  systemModules: 'systemModules',
  systemModulesFunctional: 'systemModulesFunctional',
  activeSystemModule: 'activeSystemModule',
}

const SystemModuleService = (subscribe: string[], caller: string) => {
  const subscribed: Subscription[] = []

  const [systemModuleIsLoading, handleSystemModuleIsLoading] = useState<boolean>()
  const [activeSystemModule, handleActiveSystemModule] = useState<ApiSystemModuleResult>()
  const [systemModules, handleSystemModules] = useState<ApiSystemModuleResult[]>([])
  const [systemModulesFunctional, handleSystemModulesFunctional] = useState<ApiSystemModuleResult[]>([])

  // Init effect subscribe
  useEffect(() => {
    if (!subscribe) throw Error(`Service.System.Module.${caller}: Define what you want to subscribe to`)
    if (!caller) throw Error(`Service.System.Module.${caller}: Define where you are calling from`)

    subscribe.map(s => {
      if (!Object.keys(subscribeSystemModule).includes(s))
        throw new Error(`Service.System.Module.${caller}: Can't subscribe to: ${s}`)
    })

    let isEffectStale = false

    Logger(`Service.System.Module.init.${caller}`)

    if (undefined === systemModuleStore.getValue().loading) {
      systemModuleStore.setLoading(true)
      API.get<ApiSystemModuleResult[]>(`/system/module`).then(get => {
        if (isEffectStale) return
        applyTransaction(() => {
          systemModuleStore.set(get.data)
          systemModuleStore.setLoading(false)
        })
      })
    }

    if (subscribe.includes(subscribeSystemModule.isLoading))
      subscribeIsLoading()

    if (subscribe.includes(subscribeSystemModule.activeSystemModule))
      subscribeActive()

    if (subscribe.includes(subscribeSystemModule.systemModules))
      subscribeSystemModules()

    if (subscribe.includes(subscribeSystemModule.systemModulesFunctional))
      subscribeSystemModulesFunctional()

    return () => {
      Logger(`Service.System.Module.unloading.${caller}`)
      isEffectStale = true
      subscribed.map(s => s.unsubscribe())
    }
  }, [])

  // Exported functions
  const setActiveSystemModule = (module: ApiSystemModuleResult | undefined) => {
    if (module) {
      systemModuleStore.ui.setActive(module.id)
      systemModuleStore.setActive(module.id)
    } else {
      systemModuleStore.ui.setActive(null)
      systemModuleStore.setActive(null)
    }
  }

  const getSystemModule = (id: string) => {
    const module = systemModuleQuery.getEntity(id)
    if (!module)
      throw Error(`Could not find module: ${id}`)

    return module
  }

  const getSystemModuleByName = (name: string) => {
    const module = systemModuleQuery.getAll().find(m => m.name === name)
    if (undefined === module)
      throw Error(`Could not find module: ${name}`)
    return module
  }

  const getActiveSystemModuleId = () => {
    return systemModuleQuery.getActiveId()
  }

  return {
    systemModuleIsLoading,
    activeSystemModule,
    systemModules,
    systemModulesFunctional,
    setActiveSystemModule,
    getSystemModule,
    getActiveSystemModuleId,
    getSystemModuleByName,
  }

  // Local subscribe functions
  function subscribeIsLoading() {
    subscribed.push(onEmit(systemModuleQuery.selectLoading(), newLoading => {
      handleSystemModuleIsLoading(newLoading)
    }))
  }

  function subscribeActive() {
    subscribed.push(onEmit(systemModuleQuery.selectActive(), newActive => {
      handleActiveSystemModule(newActive)
    }))
  }

  function subscribeSystemModules() {
    subscribed.push(onEmit(systemModuleQuery.selectAll(), newSystemModules => {
      handleSystemModules(newSystemModules)
    }))
  }

  function subscribeSystemModulesFunctional() {
    subscribed.push(onEmit(systemModuleQuery.selectAll(), newSystemModules => {
      const functionalModules = newSystemModules.filter(m => m.type === ModuleType.module)
      handleSystemModulesFunctional(functionalModules)
    }))
  }
}

export default SystemModuleService
